/**
 * A News-Grafiken run as a job: start it, poll it, stop it.
 *
 * A run takes one to two minutes — four graphics took 120 s, and App Runner
 * ends a request at 120 s — so no page waits on one request any more
 * (`api/infographic_api.py`). The News-Grafiken page and the Article
 * Generator share this hook, and with it the messages.
 */

import { useRef, useState } from 'react'

import { errorDetail } from '../utils/download'
import { reportSessionExpired } from '../utils/session'
import { toast } from '../utils/toast'
import { afterFailure, fetchStatus, MAX_MISSED_POLLS, usePolling } from './usePolling'

export interface Infographic {
    index: number
    kernaspekt: string
    headline: string
    subline?: string
    caption?: string
    layout_grid: string
    layout_label: string
    main_element: string
    descriptor: string
    anchor_heading?: string
    source_note?: string
    width: number
    height: number
    /** The PNG as the app serves it; the page shows and downloads it from here. */
    image_url: string
    warnings: string[]
}

export interface InfographicJobRequest {
    title: string
    body: string
    source?: string
    count?: number
    /** An Article Generator job: the graphics are attached to its article. */
    job_id?: string | null
}

interface JobSnapshot {
    status: 'running' | 'completed' | 'failed' | 'stopped'
    progress: number
    step: string | null
    infographics: Infographic[]
    error: string | null
}

export const INFOGRAPHICS_FAILED = 'Die Grafiken konnten nicht erstellt werden. Bitte noch einmal versuchen.'
const RUN_LOST = 'Der Lauf wurde nicht gefunden. Bitte neu starten.'
const STOP_FAILED = 'Der Lauf konnte nicht abgebrochen werden. Bitte noch einmal versuchen.'
const POLL_MS = 2000

export interface InfographicJob {
    running: boolean
    progress: number
    step: string | null
    results: Infographic[]
    error: string | null
    start: (request: InfographicJobRequest) => Promise<void>
    stop: () => Promise<void>
    /** Forget the graphics and any error; a run still going is stopped on the server, not left drawing. */
    reset: () => void
}

export function useInfographicJob(getToken: () => Promise<string | null>): InfographicJob {
    const [jobId, setJobId] = useState<string | null>(null)
    const [starting, setStarting] = useState(false)
    const [progress, setProgress] = useState(0)
    const [step, setStep] = useState<string | null>(null)
    const [results, setResults] = useState<Infographic[]>([])
    const [error, setError] = useState<string | null>(null)
    const missed = useRef(0)

    const headers = async (): Promise<HeadersInit> => {
        const token = await getToken()
        return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
    }

    usePolling(
        async (signal) => {
            if (!jobId) return
            const answer = await fetchStatus<JobSnapshot>(`/api/infographics/status/${jobId}`, await headers(), signal)
            if (signal.aborted) return
            if (!answer.ok) {
                if (answer.status === 404 && ++missed.current >= MAX_MISSED_POLLS) {
                    setError(RUN_LOST)
                    setJobId(null)
                }
                // A 503 means the store is busy for a moment, a 401 waits for the sign-in: the run stays.
                return afterFailure(answer)
            }
            missed.current = 0
            const job = answer.data
            setProgress(job.progress)
            setStep(job.step)
            if (job.status === 'completed') {
                setResults(job.infographics)
                setJobId(null)
            } else if (job.status === 'failed') {
                setError(job.error ?? INFOGRAPHICS_FAILED)
                setJobId(null)
            } else if (job.status === 'stopped') {
                setJobId(null)
            }
        },
        POLL_MS,
        jobId !== null,
    )

    const start = async (request: InfographicJobRequest) => {
        setStarting(true)
        setError(null)
        setResults([])
        setProgress(0)
        setStep('Wird gestartet …')
        try {
            const res = await fetch('/api/infographics/start', {
                method: 'POST',
                headers: await headers(),
                body: JSON.stringify(request),
            })
            if (res.status === 401) {
                reportSessionExpired()
                return
            }
            if (!res.ok) {
                // The server's German reason; a validation list or a proxy's
                // HTML page is no message for the editor.
                setError((await errorDetail(res)) ?? INFOGRAPHICS_FAILED)
                return
            }
            missed.current = 0
            setJobId(((await res.json()) as { job_id: string }).job_id)
        } catch (err) {
            console.error('Infographic start failed', err)
            setError(INFOGRAPHICS_FAILED)
        } finally {
            setStarting(false)
        }
    }

    /** Forgets the run only once the server agreed; a refused stop leaves it running, and followed. */
    const stop = async () => {
        if (!jobId) return
        const id = jobId
        try {
            const res = await fetch(`/api/infographics/stop/${id}`, { method: 'POST', headers: await headers() })
            if (res.status === 401) {
                reportSessionExpired()
                return
            }
            if (!res.ok) {
                toast.error((await errorDetail(res)) ?? STOP_FAILED)
                return
            }
            setJobId((current) => (current === id ? null : current))
            setStep(null)
        } catch (err) {
            console.error('Infographic stop failed', err)
            toast.error(STOP_FAILED)
        }
    }

    const reset = () => {
        if (jobId) {
            const id = jobId
            void headers()
                .then((h) => fetch(`/api/infographics/stop/${id}`, { method: 'POST', headers: h }))
                .catch((err: unknown) => console.error('Infographic stop on reset failed', err))
        }
        setJobId(null)
        setResults([])
        setError(null)
        setStep(null)
        setProgress(0)
    }

    return { running: starting || jobId !== null, progress, step, results, error, start, stop, reset }
}
